'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Lunar phase from the mean synodic month: age = days since a known new moon,
// modulo 29.530588853. This is the standard geocentric approximation, good to
// a few hours around the quarter boundaries, and the page says so honestly.

const SYNODIC_DAYS = 29.530588853;
// A well-known new moon epoch: 2000-01-06 18:14 UTC.
const EPOCH_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14, 0);

type PhaseInfo = {
  age: number;
  illumination: number;
  name: string;
  glyph: string;
  nextNewMoon: Date;
  nextFullMoon: Date;
};

const PHASES: Array<{ upTo: number; name: string; glyph: string }> = [
  { upTo: 1.0, name: 'New Moon', glyph: '🌑' },
  { upTo: 6.38, name: 'Waxing Crescent', glyph: '🌒' },
  { upTo: 8.38, name: 'First Quarter', glyph: '🌓' },
  { upTo: 13.77, name: 'Waxing Gibbous', glyph: '🌔' },
  { upTo: 15.77, name: 'Full Moon', glyph: '🌕' },
  { upTo: 21.15, name: 'Waning Gibbous', glyph: '🌖' },
  { upTo: 23.15, name: 'Last Quarter', glyph: '🌗' },
  { upTo: 28.53, name: 'Waning Crescent', glyph: '🌘' },
  { upTo: 30, name: 'New Moon', glyph: '🌑' },
];

function phaseFor(date: Date): PhaseInfo {
  const elapsed = (date.getTime() - EPOCH_NEW_MOON) / 86400000;
  const age = ((elapsed % SYNODIC_DAYS) + SYNODIC_DAYS) % SYNODIC_DAYS;
  // Illumination from the phase angle: 0 at new, 1 at full.
  const illumination = (1 - Math.cos((age / SYNODIC_DAYS) * Math.PI * 2)) / 2;
  const entry = PHASES.find((phase) => age < phase.upTo) ?? PHASES[0];

  const cyclesSinceEpoch = Math.floor(elapsed / SYNODIC_DAYS);
  const nextNewMoon = new Date(EPOCH_NEW_MOON + (cyclesSinceEpoch + 1) * SYNODIC_DAYS * 86400000);
  let fullElapsed = (cyclesSinceEpoch + 0.5) * SYNODIC_DAYS;
  if (fullElapsed * 86400000 + EPOCH_NEW_MOON <= date.getTime()) {
    fullElapsed += SYNODIC_DAYS;
  }
  const nextFullMoon = new Date(EPOCH_NEW_MOON + fullElapsed * 86400000);

  return { age, illumination, name: entry.name, glyph: entry.glyph, nextNewMoon, nextFullMoon };
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// The user's local calendar date, not UTC: for timezones far from UTC the two
// differ for part of the day, and near a phase boundary that shifts the name.
function localToday(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export default function BrowserMoonPhase() {
  const usageReported = useRef(false);
  const [dateInput, setDateInput] = useState(localToday);

  const info = useMemo(() => {
    const parsed = new Date(`${dateInput}T12:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : phaseFor(parsed);
  }, [dateInput]);

  useEffect(() => {
    if (!usageReported.current && info) {
      usageReported.current = true;
      void consumeToolUsage('moon-phase', '').catch(() => undefined);
    }
  }, [info]);

  const isToday = dateInput === localToday();

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{isToday ? 'The moon phase today' : 'Moon phase for a date'}</h2>
          <p>
            Computed from the lunar cycle right in your browser: the current phase, illumination,
            and the next new and full moons. Pick any date to look forward or back.
          </p>
        </div>
        <span className="tool-live-badge">Moon</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ alignItems: 'center', gap: '0.5rem' }}>
          {info ? (
            <>
              <span aria-hidden="true" style={{ fontSize: '7rem', lineHeight: 1.1 }}>
                {info.glyph}
              </span>
              <strong style={{ fontSize: '1.5rem' }}>{info.name}</strong>
              <span>
                {(info.illumination * 100).toFixed(0)}% illuminated · day {info.age.toFixed(1)} of
                the {SYNODIC_DAYS.toFixed(1)}-day cycle
              </span>
            </>
          ) : (
            <p className="support-feedback support-feedback-error">Pick a valid date.</p>
          )}
        </div>

        <aside className="tool-live-panel">
          <label>
            Date
            <input
              onChange={(event) => setDateInput(event.target.value)}
              type="date"
              value={dateInput}
            />
          </label>

          {info ? (
            <div className="tool-live-stats">
              <div>
                <strong>{formatDate(info.nextFullMoon)}</strong>
                <span>next full moon</span>
              </div>
              <div>
                <strong>{formatDate(info.nextNewMoon)}</strong>
                <span>next new moon</span>
              </div>
            </div>
          ) : null}

          <p className="tool-live-panel-note">
            This uses the mean lunar cycle, the standard approximation that is accurate to within a
            few hours. Exact instants of full and new moon vary slightly cycle to cycle; for
            astronomy-grade times consult an ephemeris. Phase names follow the Northern Hemisphere
            convention.
          </p>
        </aside>
      </div>
    </article>
  );
}
